import React, { useEffect } from 'react';
import Button from './ui/Button';
import { CheckCircle2, AlertTriangle, ShieldCheck, Zap, Lock, TrendingUp, BarChart3, Wallet } from 'lucide-react';

import { tracking } from '../services/tracking';

interface SalesPageProps {
  answers: number[];
}

const SalesPage: React.FC<SalesPageProps> = ({ answers }) => {
  // Score Calculation (0-15)
  // Low score = Conservative. High score = Aggressive.
  const score = answers.reduce((a, b) => a + b, 0);

  let profile = "MODERADO";
  let colorTheme = "text-brand-neon";
  let gradientTheme = "from-brand-neon to-cyan-400";

  // Custom Copy per Profile
  let headline = "Seu Plano Blindado para Lucrar com Segurança";
  let subheadline = "Você não precisa correr riscos desnecessários para vencer o mercado.";
  let problemCopy = "Você está deixando dinheiro na mesa por medo excessivo. A inflação está comendo seus ganhos enquanto você busca 'segurança demais'.";
  let chartData = [
    { name: 'Bitcoin/Ether', value: 50, color: '#3b82f6' },
    { name: 'Altcoins', value: 30, color: '#10b981' },
    { name: 'Caixa', value: 20, color: '#64748b' },
  ];

  // Determine profile BEFORE tracking
  if (score <= 6) {
    profile = "CONSERVADOR";
  } else if (score >= 11) {
    profile = "AGRESSIVO";
  } else {
    profile = "MODERADO"; // Ensure profile is set for tracking if it's moderate
  }

  useEffect(() => {
    tracking.trackEvent('sales_view', {
      score,
      profile
    });
  }, [score, profile]);

  // Reset profile to default logic for Full rendering flow (re-running logic is cheap)
  // Actually, standardizing the logic:
  if (score <= 6) { // 0-6
    profile = "CONSERVADOR";
    colorTheme = "text-blue-400";
    gradientTheme = "from-blue-400 to-indigo-500";
    headline = "Plano de Sinais: Renda Passiva & Segurança";
    subheadline = "A estratégia perfeita para quem quer dormir tranquilo e ver o patrimônio crescer.";
    problemCopy = "Você está perdendo R$ 847 por mês deixando seu dinheiro na poupança ou CDB. Enquanto você ganha 0,8%, nossos membros conservadores buscam 4-6% ao mês com proteção de capital.";
    chartData = [
      { name: 'Bitcoin (Seguro)', value: 65, color: '#3b82f6' },
      { name: 'Dólar/Stable', value: 25, color: '#64748b' },
      { name: 'Renda Fixa Crypto', value: 10, color: '#10b981' },
    ];
  } else if (score >= 11) { // 11-15
    profile = "AGRESSIVO";
    colorTheme = "text-red-500";
    gradientTheme = "from-red-500 to-orange-500";
    headline = "Plano de Ataque: Multiplicação Acelerada";
    subheadline = "Para quem tem estômago para buscar os 100x que o mercado oferece.";
    problemCopy = "Você corre risco de acordar com uma perda de R$ 5.000 ou mais por não usar stop-loss profissional. A ganância sem estratégia técnica é o caminho mais rápido para a liquidação total.";
    chartData = [
      { name: 'Gemas (Alto Risco)', value: 60, color: '#ef4444' },
      { name: 'Bitcoin Alpha', value: 20, color: '#f59e0b' },
      { name: 'Day Trade/Futuros', value: 20, color: '#8b5cf6' },
    ];
  } else {
    // Moderate (Default)
    profile = "MODERADO / BALANCEADO";
    headline = "O Equilíbrio Perfeito entre Lucro e Proteção";
    subheadline = "Apanhar as altas do mercado sem ficar exposto a quedas brutais.";
    problemCopy = "Você está deixando de ganhar R$ 1.200 a R$ 2.000 mensais por não ter uma carteira balanceada. O medo de perder te impede de multiplicar seu capital de forma consistente.";
    chartData = [
      { name: 'Blue Chips (BTC/ETH)', value: 50, color: '#3b82f6' },
      { name: 'Narrativas do Momento', value: 30, color: '#10b981' },
      { name: 'Caixa Tático', value: 20, color: '#64748b' },
    ];
  }

  return (
    <div className="min-h-screen bg-brand-dark pb-20 overflow-x-hidden relative">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Tech Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px] opacity-10"></div>
        {/* Top Spotlight */}
        <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-gradient-to-b ${gradientTheme.split(' ')[0]}/10 to-transparent blur-[120px] rounded-full`}></div>
      </div>

      <div className="relative z-10">

        {/* Top Bar Urgency */}
        <div className="bg-amber-500/10 border-b border-amber-500/20 p-2 text-center overflow-hidden">
          <p className="text-amber-400 text-xs font-bold tracking-wide uppercase flex justify-center items-center gap-2">
            <AlertTriangle size={14} />
            ⚠️ VAGAS LIMITADAS: APENAS 47 SPOTS DISPONÍVEIS PARA NOVOS MEMBROS (RESTAM 23)
          </p>
        </div>

        <div className="max-w-3xl mx-auto px-4 pt-8 space-y-12">

          {/* HERO SECTION */}
          <section className="text-center space-y-6 animate-fade-in-up">
            <div className="inline-block px-4 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-400 text-xs font-mono mb-4">
              PROTOCOLO IDENTIFICADO: <span className={colorTheme}>{profile}</span>
            </div>

            <h1 className="text-3xl md:text-5xl font-black text-white leading-tight">
              {headline}
            </h1>
            <p className="text-slate-300 text-lg md:text-xl font-light leading-relaxed max-w-xl mx-auto">
              {subheadline}
            </p>

            {/* THE PROBLEM BOX */}
            <div className="bg-slate-900/80 p-6 rounded-2xl border-l-4 border-red-500 text-left mx-auto max-w-xl shadow-2xl relative">
              <div className="absolute -top-3 -left-3 bg-red-500 text-white p-1.5 rounded-lg shadow-lg">
                <AlertTriangle size={20} />
              </div>
              <h3 className="font-bold text-white mb-2 ml-2">Diagnóstico de Risco:</h3>
              <p className="text-slate-400 leading-relaxed text-sm ml-2">
                "{problemCopy}"
              </p>
            </div>
          </section>

          {/* CHART SECTION */}
          <section className="bg-gradient-to-b from-slate-900 to-slate-950 rounded-2xl p-8 border border-slate-800 relative overflow-hidden">
            {/* Background Glow */}
            <div className={`absolute top-0 right-0 w-64 h-64 bg-gradient-to-br ${gradientTheme} opacity-10 blur-[80px] rounded-full pointing-events-none`}></div>

            <h3 className="text-center font-bold text-lg mb-8 text-white flex justify-center items-center gap-2">
              <BarChart3 className={colorTheme} size={20} />
              Sua Alocação Ideal Sugerida
            </h3>

            <div className="h-64 w-full relative z-10 flex justify-center items-center">
              {/* CSS-only Donut Chart */}
              <div
                className="w-[300px] h-[300px] rounded-full relative shadow-2xl"
                style={{
                  background: `conic-gradient(${chartData.map((item, i, arr) => {
                    const prevTotal = arr.slice(0, i).reduce((acc, curr) => acc + curr.value, 0);
                    const end = prevTotal + item.value;
                    return `${item.color} ${prevTotal}% ${end}%`;
                  }).join(', ')})`
                }}
              >
                {/* Inner Circle (Donut Hole) */}
                <div className="absolute inset-[60px] bg-slate-900 rounded-full flex items-center justify-center flex-col">
                  <span className="text-3xl font-bold text-white">{score}/15</span>
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest">Score Risco</span>
                </div>
              </div>
            </div>

            <div className="flex justify-center gap-4 text-sm flex-wrap mt-6">
              {chartData.map((d, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-lg border border-slate-700/50">
                  <div className="w-2 h-2 rounded-full shadow-[0_0_8px_currentColor]" style={{ backgroundColor: d.color, color: d.color }} />
                  <span className="text-slate-300 font-medium">{d.name} <span className="text-slate-500 text-xs">({d.value}%)</span></span>
                </div>
              ))}
            </div>
          </section>

          {/* LOW TICKET OFFER SECTION */}
          <section className="space-y-8 pt-4 pb-12">
            <div className="text-center space-y-4">
              <h2 className="text-2xl font-bold text-white">
                Pare de tentar reinventar a roda.
              </h2>
              <p className="text-slate-400 max-w-lg mx-auto">
                Nossos analistas e bots já processaram milhões de dados. Você só precisa <strong>copiar e colar.</strong>
              </p>
            </div>

            <div className="relative group">
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${gradientTheme} rounded-2xl blur opacity-30 group-hover:opacity-60 transition duration-1000`}></div>

              <div className="relative bg-slate-900 rounded-2xl border border-slate-700 p-6 md:p-8 overflow-hidden">
                {/* Label */}
                <div className="absolute top-0 right-0 bg-brand-neon text-brand-dark text-xs font-black px-4 py-2 rounded-bl-xl uppercase tracking-wider">
                  Oferta de Teste
                </div>

                <div className="flex flex-col md:flex-row gap-8 items-center md:items-start mb-8 mt-2">
                  <div className="w-16 h-16 shrink-0 bg-slate-800 rounded-2xl flex items-center justify-center border border-slate-700 text-brand-neon">
                    <Wallet size={32} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Acesso Total ao TrendFunnel VIP</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      Receba sinais de compra/venda, carteira recomendada para <strong>{profile}</strong> e alertas em tempo real.
                    </p>
                  </div>
                </div>

                <div className="space-y-4 mb-8 bg-slate-950/50 p-6 rounded-xl border border-slate-800/50">
                  <div className="flex justify-between items-center text-sm border-b border-slate-800 pb-2 border-dashed">
                    <span className="text-slate-300">✅ Sinais em Tempo Real</span>
                    <span className="text-slate-500">R$ 497/mês</span>
                  </div>
                  <div className="flex justify-between items-center text-sm border-b border-slate-800 pb-2 border-dashed">
                    <span className="text-slate-300">✅ Carteira Recomendada {profile}</span>
                    <span className="text-slate-500">R$ 297/mês</span>
                  </div>
                  <div className="flex justify-between items-center text-sm border-b border-slate-800 pb-2 border-dashed">
                    <span className="text-slate-300">✅ Plantão de Dúvidas 24h</span>
                    <span className="text-slate-500">R$ 197/mês</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-300">✅ Relatório Semanal de Mercado</span>
                    <span className="text-slate-500">R$ 97/mês</span>
                  </div>

                  <div className="pt-4 mt-2 border-t border-slate-700 flex justify-between items-center">
                    <span className="text-slate-400 font-bold uppercase text-xs tracking-wider">Valor Total</span>
                    <span className="text-slate-400 line-through decoration-red-500 decoration-2 font-bold">R$ 1.088/mês</span>
                  </div>
                </div>

                {/* PRICING STACK */}
                <div className="flex flex-col items-center justify-center pt-2">
                  <div className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-xs font-bold mb-4 border border-green-500/30 animate-pulse">
                    VOCÊ ECONOMIZA R$ 1.061 NO PRIMEIRO MÊS
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-slate-200 font-medium text-lg">Acesso Total por 7 dias:</span>
                  </div>
                  <div className="text-6xl font-black text-white tracking-tight flex items-start gap-1">
                    <span className="text-xl text-slate-500 mt-2 font-medium">R$</span>
                    27
                    <span className="text-xl text-slate-500 mt-auto mb-2 font-medium">,00</span>
                  </div>
                  <p className="text-slate-400 text-xs mt-4 mb-8 text-center max-w-xs leading-relaxed">
                    Sem fidelidade. Cancele quando quiser. <br />
                    <span className="text-brand-neon font-bold">Apenas R$ 97/mês após o teste.</span>
                  </p>

                  <Button fullWidth pulse className={`text-xl py-6 shadow-[0_0_30px_rgba(16,185,129,0.4)] bg-gradient-to-r ${gradientTheme} border-none text-white hover:brightness-110 hover:scale-[1.02] transition-transform`} onClick={() => tracking.trackEvent('offer_click', { price: 27 })}>
                    QUERO MEU PLANO AGORA
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex justify-center items-center gap-6 text-slate-500 grayscale opacity-60">
              <div className="flex items-center gap-1.5"><Lock size={12} /> <span className="text-xs font-semibold">Pagamento Seguro</span></div>
              <div className="flex items-center gap-1.5"><ShieldCheck size={12} /> <span className="text-xs font-semibold">Garantia 7 Dias</span></div>
            </div>

          </section>

        </div>
      </div>
    </div>
  );
};

export default SalesPage;